package one.healthium.log_food.ui.scanner

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.CompositingStrategy
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import one.healthium.scanner.Scanner
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.components.AppTopBarDefaults
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.red_2

interface BarcodeScannerEventDelegate {
    fun onBarcodeScanned(barcode: String)
    fun onBackClicked()
}

@Composable
fun BarcodeScannerScreen(eventDelegate: BarcodeScannerEventDelegate) {
    Box(
        Modifier.fillMaxSize()
    ) {
        Scanner(
            modifier = Modifier
                .fillMaxSize()
                .clip(RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp)),
            onScanned = {
                eventDelegate.onBarcodeScanned(it)
                true
            },
            cornerSize = 28.dp, // for iOS
        )
        ScanningOverlay()
        AppSheetTopBar(
            title = {
                Text("Scanning Barcode")
            },
            onCloseClicked = eventDelegate::onBackClicked,
            colors = AppTopBarDefaults.colors(),
        )

        Text(
            modifier = Modifier
                .align(Alignment.BottomCenter)
                .padding(bottom = 100.dp, start = 33.dp, end = 33.dp),
            text = "Place the barcode within the line to scan Search will start automatically",
            textAlign = TextAlign.Center,
            style = AppTheme.textStyles.secondary_14_400,
            color = AppTheme.colors.textInverted100,
        )
    }
}

private const val debugDrawing = false

@Composable
private fun ScanningOverlay() {
    Canvas(
        modifier = Modifier
            .fillMaxSize()
            .graphicsLayer {
                compositingStrategy = CompositingStrategy.Offscreen
            },
    ) {
        val scanAreaWidth = size.width * 0.8f // Adjust as needed
        val scanAreaHeight = size.height * 0.28f // Adjust as needed
        val scanAreaTop = (size.height - scanAreaHeight - 50.dp.toPx()) / 2
        val scanAreaLeft = (size.width - scanAreaWidth) / 2
        val cornersPadding = 12.dp.toPx()
        val holeCornerRadius = 20.dp.toPx()

        // Draw the transparent overlay around the scanning area
        drawRect(
            color = Color.Black.copy(alpha = 0.6f), // Semi-transparent black
            size = size
        )

        // Draw the transparent hole for the scanning area
        drawRoundRect(
            color = Color.Transparent,
            topLeft = Offset(scanAreaLeft + cornersPadding, scanAreaTop + cornersPadding),
            size = Size(scanAreaWidth - cornersPadding * 2, scanAreaHeight - cornersPadding * 2),
            blendMode = BlendMode.Clear,
            cornerRadius = CornerRadius(holeCornerRadius, holeCornerRadius),
        )

        // Draw the red scanning line
        val scanLineY = scanAreaTop + scanAreaHeight / 2 // Center of the scanning area
        drawLine(
            color = red_2,
            start = Offset(scanAreaLeft + cornersPadding, scanLineY),
            end = Offset(scanAreaLeft + scanAreaWidth - cornersPadding, scanLineY),
            strokeWidth = 2.dp.toPx()
        )

        // Draw the white corners of the scanning box
        val cornerSize = 30.dp.toPx()
        val cornerTile = 20.dp.toPx()
        val cornerStrokeWidth = 4.dp.toPx()
        val cornerColor = Color.White

        // Draw the white corners of the scanning box with rounded corners using Path
        val cornerPath = Path()
        val strokeStyle = Stroke(width = cornerStrokeWidth) // Stroke style for all corners

        // Top-left corner
        run {
            val startPointX = scanAreaLeft + cornerSize + cornerTile
            val startPointY = scanAreaTop

            val endPointX = scanAreaLeft
            val endPointY = scanAreaTop + cornerSize + cornerTile

            val arcRect = Rect(
                left = endPointX,
                top = startPointY,
                right = endPointX + cornerSize * 2,
                bottom = startPointY + cornerSize * 2
            )

            cornerPath.moveTo(startPointX, startPointY)
            cornerPath.arcTo(
                rect = arcRect,
                startAngleDegrees = -90f,
                sweepAngleDegrees = -90f, // Sweep counter-clockwise
                forceMoveTo = false
            )

            cornerPath.lineTo(endPointX, endPointY)
            drawPath(cornerPath, color = cornerColor, style = strokeStyle)

            if (debugDrawing) {
                drawRect(
                    color = red_2,
                    topLeft = arcRect.topLeft,
                    size = arcRect.size,
                    style = Stroke(
                        width = 4f,
                        cap = Stroke.DefaultCap,
                        join = Stroke.DefaultJoin,
                    ),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(startPointX, startPointY),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(endPointX, endPointY),
                )
            }
        }

        // Top-right corner
        run {
            val startPointX = scanAreaLeft + scanAreaWidth - cornerSize - cornerTile
            val startPointY = scanAreaTop

            val endPointX = scanAreaLeft + scanAreaWidth
            val endPointY = scanAreaTop + cornerSize + cornerTile

            val arcRect = Rect(
                left = endPointX - cornerSize * 2,
                top = startPointY,
                right = endPointX,
                bottom = startPointY + cornerSize * 2
            )

            cornerPath.moveTo(startPointX, startPointY)
            cornerPath.arcTo(
                rect = arcRect,
                startAngleDegrees = -90f,
                sweepAngleDegrees = 90f, // Sweep clockwise
                forceMoveTo = false
            )

            cornerPath.lineTo(endPointX, endPointY)
            drawPath(cornerPath, color = cornerColor, style = strokeStyle)

            if (debugDrawing) {
                drawRect(
                    color = red_2,
                    topLeft = arcRect.topLeft,
                    size = arcRect.size,
                    style = Stroke(
                        width = 4f,
                        cap = Stroke.DefaultCap,
                        join = Stroke.DefaultJoin,
                    ),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(startPointX, startPointY),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(endPointX, endPointY),
                )
            }
        }

        // Bottom-left corner
        cornerPath.reset()
        run {
            val startPointX = scanAreaLeft
            val startPointY = scanAreaTop + scanAreaHeight - cornerSize - cornerTile

            val endPointX = scanAreaLeft + cornerSize + cornerTile
            val endPointY = scanAreaTop + scanAreaHeight

            val arcRect = Rect(
                left = startPointX,
                top = endPointY - cornerSize * 2,
                right = startPointX + cornerSize * 2,
                bottom = endPointY
            )

            cornerPath.moveTo(startPointX, startPointY)
            cornerPath.arcTo(
                rect = arcRect,
                startAngleDegrees = 180f,
                sweepAngleDegrees = -90f, // Sweep counter-clockwise
                forceMoveTo = false
            )

            cornerPath.lineTo(endPointX, endPointY)
            drawPath(cornerPath, color = cornerColor, style = strokeStyle)

            if (debugDrawing) {
                drawRect(
                    color = red_2,
                    topLeft = arcRect.topLeft,
                    size = arcRect.size,
                    style = Stroke(
                        width = 4f,
                        cap = Stroke.DefaultCap,
                        join = Stroke.DefaultJoin,
                    ),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(startPointX, startPointY),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(endPointX, endPointY),
                )
            }
        }

        // Bottom-right corner
        cornerPath.reset()
        run {
            val startPointX = scanAreaLeft + scanAreaWidth - cornerSize - cornerTile
            val startPointY = scanAreaTop + scanAreaHeight

            val endPointX = scanAreaLeft + scanAreaWidth
            val endPointY = scanAreaTop + scanAreaHeight - cornerSize - cornerTile

            val arcRect = Rect(
                left = endPointX - cornerSize * 2,
                top = startPointY - cornerSize * 2,
                right = endPointX,
                bottom = startPointY
            )

            cornerPath.moveTo(startPointX, startPointY)
            cornerPath.arcTo(
                rect = arcRect,
                startAngleDegrees = 90f,
                sweepAngleDegrees = -90f, // Sweep counter-clockwise
                forceMoveTo = false
            )

            cornerPath.lineTo(endPointX, endPointY)
            drawPath(cornerPath, color = cornerColor, style = strokeStyle)

            if (debugDrawing) {
                drawRect(
                    color = red_2,
                    topLeft = arcRect.topLeft,
                    size = arcRect.size,
                    style = Stroke(
                        width = 4f,
                        cap = Stroke.DefaultCap,
                        join = Stroke.DefaultJoin,
                    ),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(startPointX, startPointY),
                )

                drawCircle(
                    color = red_2,
                    radius = 10f,
                    center = Offset(endPointX, endPointY),
                )
            }
        }
    }
}